<template>
<div>
    <div class="container-fluid">
        <div class="row">
            <div class="col-sm-12 col-xl-12" style="margin-top: 20px;">
                <div class="card">
                    <div class="card-header admin-header">
                        <div>
                            <h3>Administración</h3>
                            <p class="admin-subtitle">Cuentas, usuarios, RFCs y paquetes</p>
                        </div>
                        <button class="btn btn-outline-primary btn-sm" :disabled="loading" @click="loadData">
                            {{ loading ? 'Actualizando...' : 'Actualizar' }}
                        </button>
                    </div>
                    <div class="card-body">
                        <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

                        <div class="table-responsive" v-if="rows.length">
                            <table class="table table-lg table-hover align-middle admin-accounts-table">
                                <thead>
                                    <tr>
                                        <th>Cuenta</th>
                                        <th>Fecha registro</th>
                                        <th>Acceso social</th>
                                        <th>Último login</th>
                                        <th>Usuarios</th>
                                        <th>RFCs</th>
                                        <th>CFDIs</th>
                                        <th style="min-width: 250px;">Paquete</th>
                                        <th>Vigencia</th>
                                        <th>Próximo pago</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in rows" :key="item.id">
                                        <td>
                                            <div>
                                                <div class="fw-bold">{{ item.name }}</div>
                                                <small class="text-muted account-email">{{ item.email }}</small>
                                                <div class="d-flex gap-1 account-actions mt-2">
                                                    <button
                                                        class="btn btn-outline-primary btn-sm"
                                                        :disabled="impersonating[item.id] || loading"
                                                        @click="impersonateAccount(item)"
                                                        title="Acceder temporalmente"
                                                        aria-label="Acceder temporalmente"
                                                    >
                                                        <i class="fa fa-sign-in"></i>
                                                        {{ impersonating[item.id] ? 'Accediendo...' : 'Acceder' }}
                                                    </button>
                                                    <button
                                                        class="btn btn-outline-danger btn-sm"
                                                        @click="openDeleteModal(item)"
                                                        title="Eliminar cuenta"
                                                        aria-label="Eliminar cuenta"
                                                    >
                                                        <i class="fa fa-trash"></i>
                                                        Eliminar
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{{ formatDateTime(item.created_at) }}</td>
                                        <td>
                                            <div v-if="socialBadges(item).length" class="social-badges-group">
                                                <span
                                                    v-for="(badge, index) in socialBadges(item)"
                                                    :key="`${item.id}-social-${index}-${badge.type}`"
                                                    class="badge social-badge"
                                                    :class="`social-badge-${badge.type}`"
                                                >
                                                    {{ badge.text }}
                                                </span>
                                            </div>
                                            <span v-else>-</span>
                                        </td>
                                        <td>{{ formatDateTime(item.last_login_at) }}</td>
                                        <td>{{ item.users_count || 0 }}</td>
                                        <td>{{ item.rfcs_count || 0 }}</td>
                                        <td>{{ item.cfdis_count || 0 }}</td>
                                        <td>
                                            <div class="d-flex gap-2">
                                                <select class="form-select form-select-sm" v-model="selectedPackages[item.id]">
                                                    <option v-for="pkg in packages" :key="pkg.id" :value="pkg.id">
                                                        {{ packageLabel(pkg) }}
                                                    </option>
                                                </select>
                                                <button
                                                    class="btn btn-primary btn-sm"
                                                    :disabled="saving[item.id] || !selectedPackages[item.id] || Number(selectedPackages[item.id]) === Number(item.package_id)"
                                                    @click="changePackage(item)"
                                                >
                                                    {{ saving[item.id] ? 'Guardando...' : 'Guardar' }}
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="d-flex gap-2">
                                                <input
                                                    type="date"
                                                    class="form-control form-control-sm"
                                                    :value="currentValidityDate(item)"
                                                    @input="onValidityInput(item.id, $event.target.value)"
                                                >
                                                <button
                                                    class="btn btn-outline-primary btn-sm"
                                                    :disabled="savingValidity[item.id] || !currentValidityDate(item) || currentValidityDate(item) === toDateInput(item.ends_at)"
                                                    @click="changeValidity(item)"
                                                >
                                                    {{ savingValidity[item.id] ? 'Guardando...' : 'Guardar' }}
                                                </button>
                                            </div>
                                        </td>
                                        <td>{{ formatNextPayment(item) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div v-else-if="!loading" class="text-center py-4">
                            <h6>No hay cuentas para mostrar.</h6>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="deleteModalVisible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Eliminar Cuenta</h5>
                    <button type="button" class="btn-close" aria-label="Close" @click="closeDeleteModal"></button>
                </div>
                <div class="modal-body">
                    <div v-if="deleteModalError" class="alert alert-danger" role="alert">{{ deleteModalError }}</div>

                    <div v-if="deleteModalLoading" class="text-muted">Cargando información de la cuenta...</div>

                    <div v-else-if="deleteSummary">
                        <p class="mb-2"><strong>Cuenta:</strong> {{ deleteSummary.name || '-' }}</p>
                        <p class="mb-2"><strong>Email:</strong> {{ deleteSummary.email || '-' }}</p>
                        <p class="mb-2"><strong>Usuarios:</strong> {{ deleteSummary.users_count || 0 }}</p>
                        <p class="mb-2"><strong>RFCs cargados:</strong> {{ deleteSummary.rfcs_count || 0 }}</p>
                        <p class="mb-2"><strong>CFDIs cargados:</strong> {{ deleteSummary.cfdis_count || 0 }}</p>
                        <p class="mb-3"><strong>Requests cargados:</strong> {{ deleteSummary.requests_count || 0 }}</p>

                        <label class="form-label" for="delete-confirmation-word">Escribe <strong>ELIMINAR</strong> para confirmar</label>
                        <input
                            id="delete-confirmation-word"
                            v-model="deleteConfirmationWord"
                            type="text"
                            class="form-control"
                            placeholder="ELIMINAR"
                            autocomplete="off"
                        >
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" :disabled="deleteModalDeleting" @click="closeDeleteModal">Cancelar</button>
                    <button
                        type="button"
                        class="btn btn-danger"
                        :disabled="deleteModalDeleting || deleteModalLoading || !deleteSummary || !isDeletionWordValid()"
                        @click="confirmDeleteAccount"
                    >
                        {{ deleteModalDeleting ? 'Eliminando...' : 'Eliminar cuenta' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div v-if="deleteModalVisible" class="modal-backdrop fade show"></div>
</div>
</template>

<script>
import accountService from '@/services/account';

export default {
    data() {
        return {
            loading: false,
            error: '',
            rows: [],
            packages: [],
            selectedPackages: {},
            saving: {},
            selectedValidityDates: {},
            savingValidity: {},
            impersonating: {},
            deleteModalVisible: false,
            deleteModalLoading: false,
            deleteModalDeleting: false,
            deleteModalError: '',
            deleteSummary: null,
            deleteTargetId: null,
            deleteConfirmationWord: '',
        }
    },
    mounted: async function(){
        await this.loadData();
    },
    methods: {
        ensureAdminAccess() {
            const rawUser = localStorage.getItem('user');
            if (!rawUser || rawUser === 'undefined') {
                this.$router.replace({ name: 'dashboard' });
                return false;
            }

            try {
                const user = JSON.parse(rawUser) || {};
                if (!Boolean(user.is_admin)) {
                    this.$router.replace({ name: 'dashboard' });
                    return false;
                }
            } catch (error) {
                this.$router.replace({ name: 'dashboard' });
                return false;
            }

            return true;
        },
        async loadData(){
            if (!this.ensureAdminAccess()) {
                return;
            }

            this.loading = true;
            this.error = '';

            try{
                const response = await accountService.getAdminAccountsSummary();
                if(response && response.type === 'success'){
                    this.rows = Array.isArray(response?.data?.accounts) ? response.data.accounts : [];
                    this.packages = Array.isArray(response?.data?.packages) ? response.data.packages : [];

                    const nextMap = {};
                    const validityMap = {};
                    this.rows.forEach((item) => {
                        nextMap[item.id] = Number(item.package_id || 0) || null;
                        validityMap[item.id] = this.toDateInput(item.ends_at);
                    });
                    this.selectedPackages = nextMap;
                    this.selectedValidityDates = validityMap;
                    return;
                }

                this.rows = [];
                this.error = response?.message || 'No fue posible cargar el listado de cuentas.';
            }
            catch(error){
                this.rows = [];
                this.error = error?.response?.data?.message || 'No fue posible cargar el listado de cuentas.';
            }
            finally{
                this.loading = false;
            }
        },
        async changePackage(item){
            const packageId = Number(this.selectedPackages[item.id] || 0);
            if (!packageId) {
                return;
            }

            this.error = '';

            this.saving = {
                ...this.saving,
                [item.id]: true,
            };

            try {
                const response = await accountService.updateAdminAccountPackage(item.id, packageId);
                if (response && response.type === 'success') {
                    this.$toast.success('Paquete actualizado correctamente.');
                    await this.loadData();
                    return;
                }

                this.error = response?.message || 'No fue posible cambiar el paquete.';
            } catch (error) {
                this.error = error?.response?.data?.message || 'No fue posible cambiar el paquete.';
            } finally {
                this.saving = {
                    ...this.saving,
                    [item.id]: false,
                };
            }
        },
        getCurrentPeriod() {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');

            return `${year}-${month}`;
        },
        buildAdminBackup(item) {
            const accessToken = String(localStorage.getItem('access_token') || '').trim();
            const rawUser = localStorage.getItem('user');

            if (!accessToken || !rawUser || rawUser === 'undefined') {
                return null;
            }

            let parsedUser = null;
            try {
                parsedUser = JSON.parse(rawUser) || null;
            } catch (_) {
                parsedUser = null;
            }

            if (!parsedUser || !parsedUser.is_admin) {
                return null;
            }

            return {
                admin_token: accessToken,
                admin_user: parsedUser,
                admin_rfcs: localStorage.getItem('rfcs') || '[]',
                admin_rfc: localStorage.getItem('rfc') || '',
                admin_fiscal_name: localStorage.getItem('fiscal_name') || '',
                admin_period: localStorage.getItem('period') || this.getCurrentPeriod(),
                started_at: new Date().toISOString(),
                target_account_id: Number(item?.id || 0) || null,
                target_account_name: String(item?.name || '').trim(),
            };
        },
        applyImpersonatedSession(payload) {
            const token = String(payload?.token || '').trim();
            const user = payload?.user || null;

            if (!token || !user) {
                throw new Error('No se recibió información de sesión para acceso temporal.');
            }

            localStorage.setItem('access_token', token);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('rfcs', JSON.stringify(user.rfcs || []));
            localStorage.setItem('period', localStorage.getItem('period') || this.getCurrentPeriod());

            if (user.rfc && user.rfc.rfc) {
                localStorage.setItem('rfc', user.rfc.rfc);
                localStorage.setItem('fiscal_name', user.rfc.name || '');
            } else {
                localStorage.setItem('rfc', '');
                localStorage.setItem('fiscal_name', '');
            }

            localStorage.setItem('admin_impersonation_context', JSON.stringify(payload?.impersonation || {}));
        },
        async impersonateAccount(item) {
            const accountId = Number(item?.id || 0);
            if (!accountId) {
                return;
            }

            this.error = '';
            this.impersonating = {
                ...this.impersonating,
                [accountId]: true,
            };

            try {
                const existingBackup = localStorage.getItem('admin_impersonation_backup');
                if (!existingBackup) {
                    const backup = this.buildAdminBackup(item);
                    if (backup) {
                        localStorage.setItem('admin_impersonation_backup', JSON.stringify(backup));
                    }
                }

                const response = await accountService.impersonateAdminAccount(accountId);
                if (response && response.type === 'success') {
                    this.applyImpersonatedSession(response.data || {});
                    this.$toast.success('Acceso temporal iniciado. Estás visualizando la cuenta seleccionada.');
                    await this.$router.push({ name: 'dashboard' });
                    return;
                }

                this.error = response?.message || 'No fue posible iniciar el acceso temporal.';
            } catch (error) {
                this.error = error?.response?.data?.message || 'No fue posible iniciar el acceso temporal.';
            } finally {
                this.impersonating = {
                    ...this.impersonating,
                    [accountId]: false,
                };
            }
        },
        async changeValidity(item){
            const endsAt = String(this.currentValidityDate(item) || '').trim();
            if (!endsAt) {
                return;
            }

            this.error = '';

            this.savingValidity = {
                ...this.savingValidity,
                [item.id]: true,
            };

            try {
                const response = await accountService.updateAdminAccountValidity(item.id, endsAt);
                if (response && response.type === 'success') {
                    const updatedEndsAt = response?.data?.ends_at || `${endsAt} 23:59:59`;
                    const rowIndex = this.rows.findIndex((row) => Number(row.id) === Number(item.id));
                    if (rowIndex >= 0) {
                        this.rows[rowIndex] = {
                            ...this.rows[rowIndex],
                            ends_at: updatedEndsAt,
                            next_payment_at: response?.data?.next_payment_at || null,
                            conekta_subscription_id: response?.data?.conekta_subscription_id || this.rows[rowIndex].conekta_subscription_id || null,
                        };
                    }
                    this.selectedValidityDates = {
                        ...this.selectedValidityDates,
                        [item.id]: this.toDateInput(updatedEndsAt),
                    };
                    this.$toast.success('Vigencia actualizada correctamente.');
                    return;
                }

                this.error = response?.message || 'No fue posible actualizar la vigencia.';
            } catch (error) {
                this.error = error?.response?.data?.message || 'No fue posible actualizar la vigencia.';
            } finally {
                this.savingValidity = {
                    ...this.savingValidity,
                    [item.id]: false,
                };
            }
        },
        async openDeleteModal(item){
            this.deleteModalVisible = true;
            this.deleteModalLoading = true;
            this.deleteModalDeleting = false;
            this.deleteModalError = '';
            this.deleteSummary = null;
            this.deleteTargetId = Number(item?.id || 0) || null;
            this.deleteConfirmationWord = '';

            if (!this.deleteTargetId) {
                this.deleteModalLoading = false;
                this.deleteModalError = 'No fue posible identificar la cuenta seleccionada.';
                return;
            }

            try {
                const response = await accountService.getAdminAccountDeletionSummary(this.deleteTargetId);
                if (response && response.type === 'success') {
                    this.deleteSummary = response?.data?.account || null;
                    return;
                }

                this.deleteModalError = response?.message || 'No fue posible cargar el resumen de eliminación.';
            } catch (error) {
                this.deleteModalError = error?.response?.data?.message || 'No fue posible cargar el resumen de eliminación.';
            } finally {
                this.deleteModalLoading = false;
            }
        },
        closeDeleteModal(){
            this.deleteModalVisible = false;
            this.deleteModalLoading = false;
            this.deleteModalDeleting = false;
            this.deleteModalError = '';
            this.deleteSummary = null;
            this.deleteTargetId = null;
            this.deleteConfirmationWord = '';
        },
        isDeletionWordValid(){
            return String(this.deleteConfirmationWord || '').trim() === 'ELIMINAR';
        },
        async confirmDeleteAccount(){
            if (!this.deleteTargetId || !this.isDeletionWordValid()) {
                return;
            }

            this.deleteModalError = '';
            this.deleteModalDeleting = true;
            this.error = '';

            try {
                const response = await accountService.deleteAdminAccount(this.deleteTargetId, this.deleteConfirmationWord);
                if (response && response.type === 'success') {
                    this.$toast.success('Cuenta eliminada correctamente.');
                    this.closeDeleteModal();
                    await this.loadData();
                    return;
                }

                this.deleteModalError = response?.message || 'No fue posible eliminar la cuenta.';
            } catch (error) {
                this.deleteModalError = error?.response?.data?.message || 'No fue posible eliminar la cuenta.';
            } finally {
                this.deleteModalDeleting = false;
            }
        },
        packageLabel(pkg){
            const limit = pkg?.rfc_limit === null || pkg?.rfc_limit === undefined
                ? 'RFCs ilimitados'
                : `${pkg.rfc_limit} RFC${Number(pkg.rfc_limit) === 1 ? '' : 's'}`;

            return `${pkg?.name || 'Paquete'} - ${limit}`;
        },
        packageMeta(item){
            const limit = item?.package_rfc_limit;
            if (limit === null || limit === undefined) {
                return 'RFCs ilimitados';
            }

            return `${limit} RFC${Number(limit) === 1 ? '' : 's'}`;
        },
        formatDate(value){
            if(!value) return 'Sin fecha';

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return 'Sin fecha';

            return date.toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            });
        },
        formatDateTime(value){
            if(!value) return '-';

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return '-';

            return date.toLocaleString('es-MX', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            });
        },
        toDateInput(value){
            if (!value) return '';

            const raw = String(value).trim();
            const directMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
            if (directMatch) {
                return directMatch[1];
            }

            const date = new Date(raw);
            if (Number.isNaN(date.getTime())) return '';

            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
        currentValidityDate(item){
            const typedValue = this.selectedValidityDates[item.id];
            if (typedValue) {
                return typedValue;
            }

            return this.toDateInput(item?.ends_at || null);
        },
        onValidityInput(accountId, value){
            this.selectedValidityDates = {
                ...this.selectedValidityDates,
                [accountId]: value,
            };
        },
        formatNextPayment(item){
            if (!item?.conekta_subscription_id) {
                return '-';
            }

            return this.formatDate(item.next_payment_at);
        },
        providerStatusLabel(hasRegistered, hasLogin){
            if (hasRegistered && hasLogin) {
                return 'registro + login';
            }

            if (hasRegistered) {
                return 'registro';
            }

            if (hasLogin) {
                return 'login';
            }

            return '';
        },
        socialBadges(item){
            const hasGoogleLogin = Boolean(item?.has_google_login_user);
            const hasFacebookLogin = Boolean(item?.has_facebook_login_user);
            const googleStatus = this.providerStatusLabel(
                Boolean(item?.has_google_registered_user),
                hasGoogleLogin
            );
            const facebookStatus = this.providerStatusLabel(
                Boolean(item?.has_facebook_registered_user),
                hasFacebookLogin
            );

            const badges = [];

            if (hasGoogleLogin && hasFacebookLogin) {
                badges.push({
                    type: 'both',
                    text: 'Login en ambos',
                });
            }

            if (googleStatus) {
                badges.push({
                    type: 'google',
                    text: `Google: ${googleStatus}`,
                });
            }

            if (facebookStatus) {
                badges.push({
                    type: 'facebook',
                    text: `Facebook: ${facebookStatus}`,
                });
            }

            return badges;
        }
    }
}
</script>

<style scoped>
.admin-header{
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.admin-subtitle{
    margin: 4px 0 0;
    font-size: 12px;
    color: #6c757d;
}

.account-email {
    text-transform: lowercase !important;
}

.admin-accounts-table th {
    font-size: 11px;
    padding: 6px 8px !important;
    white-space: nowrap;
}

.admin-accounts-table td {
    font-size: 11px;
    padding: 6px 8px !important;
}

.admin-accounts-table .fw-bold {
    font-size: 12px;
    line-height: 1.2;
}

.admin-accounts-table .account-email {
    font-size: 10px;
}

.admin-accounts-table .badge {
    font-size: 10px;
}

.admin-accounts-table .social-badge {
    display: inline-block;
    border: 1px solid transparent;
    font-weight: 600;
}

.admin-accounts-table .social-badges-group {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.admin-accounts-table .social-badge-both {
    background-color: #f3f4f6 !important;
    color: #374151 !important;
    border-color: #d1d5db;
}

.admin-accounts-table .social-badge-google {
    background-color: #fde8e8 !important;
    color: #991b1b !important;
    border-color: #f8b4b4;
}

.admin-accounts-table .social-badge-facebook {
    background-color: #e8f1ff !important;
    color: #0d3b78 !important;
    border-color: #b6ccf3;
}

.admin-accounts-table .btn {
    font-size: 10px;
    line-height: 1.2;
    padding: 3px 6px;
}

.admin-accounts-table .account-actions .btn {
    white-space: nowrap;
}

.admin-accounts-table .form-select,
.admin-accounts-table .form-control {
    font-size: 10px;
    line-height: 1.2;
    padding: 3px 6px;
    min-height: 28px;
}
</style>
